import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  mode: ThemeMode;
  isDark: boolean;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const getTimeBasedTheme = (): 'light' | 'dark' => {
  const hour = new Date().getHours();
  // 6 AM (6) to 6 PM (18) = light mode
  // 6 PM (18) to 6 AM (6) = dark mode
  return hour >= 6 && hour < 18 ? 'light' : 'dark';
};

const applyTheme = (isDark: boolean) => {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('auto');
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Load saved theme from localStorage or default to 'auto'
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      setMode(savedTheme);
    }
  }, []);

  useEffect(() => {
    let newIsDark: boolean;

    if (mode === 'auto') {
      newIsDark = getTimeBasedTheme() === 'dark';
    } else {
      newIsDark = mode === 'dark';
    }

    setIsDark(newIsDark);
    applyTheme(newIsDark);
  }, [mode]);

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
    localStorage.setItem('theme', newMode);
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        isDark,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};